import React from 'react';
import { Info } from '@phosphor-icons/react';
import { CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { QuestionItem } from './types';

interface ExtractionConfigSectionProps {
  question: QuestionItem;
  questionIndex: number;
  onUpdate: (index: number, updates: Partial<QuestionItem>) => void;
}

const VALUE_TYPES = [
  { value: 'number' as const, label: 'Getal' },
  { value: 'string' as const, label: 'Tekst' },
  { value: 'date' as const, label: 'Datum' },
  { value: 'boolean' as const, label: 'Ja/Nee' },
];

export const ExtractionConfigSection: React.FC<ExtractionConfigSectionProps> = ({
  question,
  questionIndex,
  onUpdate,
}) => {
  const getPlaceholder = () => {
    switch (question.extractionValueType) {
      case 'number':
        return 'bijv. Hoeveel wooneenheden worden vergund in dit document?';
      case 'date':
        return 'bijv. Wat is de datum van de vergunning?';
      case 'boolean':
        return 'bijv. Is de aanvraag goedgekeurd?';
      default:
        return 'bijv. Wat is de naam van de aanvrager?';
    }
  };

  return (
    <CardContent className="pt-4 border-t">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-medium text-violet-700">Extractie Configuratie</span>
          <div className="relative group">
            <Info size={14} className="text-violet-400 cursor-help" />
            <div className="absolute left-0 bottom-full mb-2 w-80 p-3 bg-neutral-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <p className="font-semibold mb-2">Wat is een extractie vraag?</p>
              <p className="text-neutral-300 mb-2">
                Een extractie vraag haalt specifieke informatie uit documenten en slaat deze op voor gebruik in andere
                vragen. Denk aan: aantal wooneenheden uit een vergunning, oppervlakte uit een plan.
              </p>
              <p className="font-semibold mb-1">Voorbeeld workflow:</p>
              <ol className="text-neutral-300 list-decimal pl-4 space-y-1">
                <li>Vraag 1 (extractie): &quot;Hoeveel wooneenheden staan in de vergunning?&quot; → extraheert: 5</li>
                <li>Vraag 2 (status): &quot;Klopt het aantal?&quot; → vergelijkt 5 met database → groen/rood bolletje</li>
              </ol>
              <div className="absolute left-4 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-neutral-800" />
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Extraheert een waarde uit documenten voor gebruik in opvolgende vragen. Deze vraag toont{' '}
          <strong className="text-violet-600">geen status bolletje</strong> in het overzicht.
        </p>
      </div>

      <div className="space-y-4 p-3 bg-violet-50/50 rounded-lg border border-violet-100">
        {/* Value Type Selector */}
        <div>
          <Label className="text-xs text-violet-700 mb-1.5 block">
            Type waarde om te extraheren
            <span className="text-[10px] text-muted-foreground ml-2 font-normal">
              (Bepaalt hoe de AI de waarde interpreteert)
            </span>
          </Label>
          <div className="flex gap-2">
            {VALUE_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => onUpdate(questionIndex, { extractionValueType: type.value })}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  question.extractionValueType === type.value
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-violet-200 text-violet-700 hover:bg-violet-100'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
          {question.extractionValueType === 'number' && (
            <p className="text-[10px] text-violet-600 mt-1.5">
              Bij &quot;Getal&quot; wordt de AI geforceerd om alleen een nummer terug te geven (bijv. &quot;5&quot;, niet
              &quot;5 wooneenheden&quot;).
            </p>
          )}
        </div>

        {/* Extraction Label */}
        <div>
          <Label className="text-xs text-violet-700 mb-1.5 block">
            Weergavenaam voor geëxtraheerde waarde
            <span className="text-[10px] text-muted-foreground ml-2 font-normal">(Zichtbaar voor gebruikers)</span>
          </Label>
          <Input
            placeholder="bijv. Aantal vergunde wooneenheden"
            value={question.extractionLabel || ''}
            onChange={(e) => onUpdate(questionIndex, { extractionLabel: e.target.value })}
            className="text-sm"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Dit label wordt getoond in het due diligence overzicht naast de geëxtraheerde waarde.
          </p>
        </div>

        {/* Extraction Prompt */}
        <div>
          <Label className="text-xs text-violet-700 mb-1.5 block">
            AI Extractie Instructie
            <span className="text-[10px] text-muted-foreground ml-2 font-normal">(Instructie voor de AI)</span>
          </Label>
          <Textarea
            placeholder={getPlaceholder()}
            value={question.extractionPrompt || ''}
            onChange={(e) => onUpdate(questionIndex, { extractionPrompt: e.target.value })}
            className="text-sm min-h-[80px]"
          />
          <div className="text-[10px] text-muted-foreground mt-1 space-y-1">
            <p>Schrijf een duidelijke vraag die de AI moet beantwoorden op basis van het document.</p>
            {question.extractionValueType === 'number' && (
              <p className="text-violet-600">
                <strong>Tip:</strong> De AI wordt geforceerd om alleen een getal terug te geven. Je hoeft niet te
                schrijven &quot;geef alleen het getal&quot;.
              </p>
            )}
          </div>
        </div>
      </div>
    </CardContent>
  );
};
